import {
  useContext,
  useRef,
  forwardRef,
  ComponentPropsWithoutRef,
} from "react";
import { SegmentedControlContext } from "./SegmentedControl";
import styles from "./SegmentedControl.module.scss";
import { Icon, IconProps } from "../Icon";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";
import { motion, useReducedMotion } from "motion/react";
import { core } from "@servicetitan/hammer-token";

/**
 * State object returned by segment interactions
 */
export type SegmentState = {
  /**
   * Whether the segment is checked
   */
  checked: boolean;
  /**
   * The value of the segment
   */
  value?: SegmentedControlSegmentProps["value"];
};

/**
 * Props for the SegmentedControlSegment component
 * @extends ComponentPropsWithoutRef<"label">
 */
export type SegmentedControlSegmentProps = ComponentPropsWithoutRef<"label"> & {
  /**
   * Optional icon to display in the segment
   */
  icon?: IconProps["svg"];
  /**
   * Value of the segment
   */
  value: string;
  /**
   * The content to display in the segment
   */
  children?: React.ReactNode;
};

/**
 * SegmentedControlSegment component for individual segments within a segmented control.
 *
 * Features:
 * - Individual segment with radio input
 * - Optional icon support
 * - Animated selection indicator
 * - Accessibility support with proper ARIA attributes
 * - Context integration with parent SegmentedControl
 * - Motion animations with reduced motion support
 * - Icon-only mode when no children provided
 *
 * @example
 * <SegmentedControl.Segment value="option1" icon={Icon1}>
 *   Option 1
 * </SegmentedControl.Segment>
 *
 * @example
 * <SegmentedControl.Segment value="icon-only" icon={IconOnly} />
 */
export const SegmentedControlSegment = forwardRef<
  HTMLLabelElement,
  SegmentedControlSegmentProps
>((props, ref) => {
  const { icon, value, children, className, ...rest } = props;
  const shouldReduceMotion = useReducedMotion();
  const { current, setCurrent, name } = useContext(SegmentedControlContext);
  const labelRef = useRef<HTMLSpanElement>(null);

  const classNames = cx(styles.segment, className, {
    [styles["icon-only"]]: icon && !children,
  });

  return (
    <>
      <label
        className={classNames}
        ref={useMergeRefs([labelRef, ref])}
        data-anv="segmented-control-segment"
        {...rest}
      >
        <input
          type="radio"
          name={name}
          value={value}
          onChange={() => setCurrent?.(value)}
          checked={current === value}
          aria-checked={current === value}
        />
        {icon ? <Icon svg={icon} aria-hidden /> : null}
        {children}
        {current === value && (
          <motion.span
            layoutId={name}
            className={styles["select-indicator"]}
            transition={{
              duration: shouldReduceMotion
                ? 0
                : +core.primitive.Duration.value.replace("ms", "") / 1000,
            }}
          />
        )}
      </label>
    </>
  );
});

SegmentedControlSegment.displayName = "SegmentedControlSegment";
